import { useQuery } from '@apollo/client';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GetAudiencePricingDocument } from '../../../../graphql/query/audiences/GetAudiencePricing.generated.tsx';
import { useDataPartnerNames } from '../../hooks/useDataPartnerNames.ts';
import { FeeSummary } from '../components/MultiPartnerPricing/FeeSummary.tsx';
import { PartnerPricing } from '../components/MultiPartnerPricing/PartnerPricing.tsx';
import { MatchingOperator } from '../constants.ts';
import type { AudienceForm } from '../types.ts';
import { filterNormalizedEmptyGroups } from '../utils/filterNormalizedEmptyGroups.ts';
import { hasMoreThanOneGroupOrSubGroup } from '../utils/hasMoreThanOneGroupOrSubGroup.ts';
import { hasSegmentDataInIncludes } from '../utils/hasSegmentDataInIncludes.ts';

export type AudiencePricingResult = {
  computePricing: {
    pricing_by_org: Record<
      string,
      {
        price_pom: number;
        price_cpm: number;
      }
    >;
    operator: MatchingOperator;
    totals: {
      price_pom: number;
      price_cpm: number;
    };
  };
};

export const MultiPartnerPricing = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const observableAccountId = useWatch<AudienceForm, 'account_id'>({
    name: 'account_id',
  });

  const observableNormalizedDefinition = useWatch<AudienceForm, 'normalized_definition'>({
    name: 'normalized_definition',
  });

  const filteredDefinition = filterNormalizedEmptyGroups(observableNormalizedDefinition);
  const hasMoreGroupsThanOne = hasMoreThanOneGroupOrSubGroup(filteredDefinition);

  const {
    data,
    loading: pricingLoading,
    error: pricingError,
  } = useQuery(GetAudiencePricingDocument, {
    variables: {
      accountId: observableAccountId,
      normalizedDefinition: filteredDefinition,
    },
    skip: !hasSegmentDataInIncludes(observableNormalizedDefinition),
  });

  const { partnerNames, loading: dataPartnersLoading, error: dataPartnersError } = useDataPartnerNames();

  useEffect(() => {
    if (pricingError) {
      enqueueSnackbar(t('Could not get pricing data from the server'), { variant: 'error' });
    }
  }, [pricingError, enqueueSnackbar, t]);

  if (pricingLoading || !data) {
    return (
      <FormSection
        data-test="pricing-title-section"
        title={t('Multi Partner Audience Pricing')}
        rightColumnSize={RIGHT_COLUMN_WIDTH}
      >
        <Loader />
      </FormSection>
    );
  }

  const pricingByOrg = data?.computePricing?.pricing_by_org ?? {};
  const totalFees = data?.computePricing?.totals ?? { price_pom: 0, price_cpm: 0 };
  const pricingOperator = data?.computePricing?.operator ?? MatchingOperator.UNION;

  return (
    <FormSection
      data-test="pricing-title-section"
      title={t('Multi Partner Audience Pricing')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <PartnerPricing
        prices={pricingByOrg}
        partnerNames={partnerNames}
        loading={dataPartnersLoading}
        error={dataPartnersError}
      />
      <FeeSummary totalFees={totalFees} operator={hasMoreGroupsThanOne ? pricingOperator : MatchingOperator.UNION} />
    </FormSection>
  );
};
